// src/components/PlotComponent/PlotComponent.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import getChartOptions from '../getChartOptions';
import { useSelectedChannels } from '../../contexts/SelectedChannelsContext';
import styles from './PlotComponent.module.css';
import PsdComponent from '../PsdComponent/PsdComponent';

// Default data loader — can be overridden in props
async function defaultLoadData({ backendUrl, user, channelIds, start, end, points = 2000 }) {
  const res = await axios.get(`${backendUrl}/data/channels`, {
    headers: { Authorization: `Bearer ${user.token}` },
    params: { id: channelIds, start, end, points }
  });
  return res.data; // { [label]: { timestamp:number[], min_value:number[], max_value:number[] }, ... }
}

// Stable color by label
function colorFromLabel(label) {
  let hash = 5383;
  for (let i = 0; i < label.length; i++) hash = ((hash << 5) + hash) + label.charCodeAt(i);
  let t = (hash >>> 0) + 0x6D2B79F5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const rnd = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  const h = Math.floor(rnd * 360), s = 78, l = 68;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function getChannelLabel(channel) {
  if (!channel) return null;
  if (channel.fullname) return channel.fullname;
  if (channel.source && channel.name) return `${channel.source}.${channel.name}`;
  if (channel.name) return channel.name;
  return String(channel.id);
}

function getChannelDisplayLabel(label, mappings) {
  for (const mapping of mappings ?? []) {
    const { pattern, displayName } = mapping ?? {};
    if (!(pattern instanceof RegExp)) continue;

    // RegExp.test() is stateful for global/sticky expressions, so ensure each
    // channel name is tested from the beginning.
    pattern.lastIndex = 0;
    if (!pattern.test(label)) continue;
    pattern.lastIndex = 0;

    return typeof displayName === 'function'
      ? displayName(label)
      : (displayName ?? label);
  }

  return label;
}

function getValueTypeKey(channel) {
  return channel?.value_type_id != null ? String(channel.value_type_id) : '__unknown__';
}

function getValueTypeName(channel) {
  return channel?.value_type_name || 'Value';
}

function getValueTypeUnit(channel) {
  return channel?.value_type_unit || '';
}

function getYAxisLabel(channel) {
  const name = getValueTypeName(channel);
  const unit = getValueTypeUnit(channel);
  return unit ? `${name} [${unit}]` : name;
}

function toPlotValue(value) {
  if (value === null || typeof value === 'undefined') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function buildSeriesRows(series) {
  const timestamps = Array.isArray(series?.timestamp) ? series.timestamp : [];
  const hasBand =
    Array.isArray(series?.min_value) &&
    Array.isArray(series?.max_value);
  const hasRawValues = Array.isArray(series?.value);
  const rows = [];
  const minRows = [];
  const maxRows = [];
  let hasGap = false;

  for (let i = 0; i < timestamps.length; i++) {
    const timestampSeconds = Number(timestamps[i]);
    if (!Number.isFinite(timestampSeconds)) continue;

    const timestamp = new Date(timestampSeconds * 1000);

    if (hasBand) {
      const minValue = toPlotValue(series.min_value[i]);
      const maxValue = toPlotValue(series.max_value[i]);

      if (minValue === null || maxValue === null) {
        minRows.push({ x: timestamp, y: null });
        maxRows.push({ x: timestamp, y: null });
        hasGap = true;
        continue;
      }

      minRows.push({ x: timestamp, y: minValue });
      maxRows.push({ x: timestamp, y: maxValue });
      continue;
    }

    if (hasRawValues) {
      const value = toPlotValue(series.value[i]);
      if (value === null) hasGap = true;
      rows.push({ x: timestamp, y: value });
    }
  }

  const separator = maxRows.length > 0
    ? [{ x: maxRows.at(-1).x, y: null }]
    : [];
  const seriesRows = hasBand
    ? [...maxRows, ...separator, ...[...minRows].reverse()]
    : rows;

  return { rows: seriesRows, hasBand, hasGap: hasGap || (hasBand && separator.length > 0) };
}

function createPlotPanel(channel, key) {
  return {
    key,
    title: getValueTypeName(channel),
    yAxisLabel: getYAxisLabel(channel),
    datasets: [],
  };
}

export default function PlotComponent({
  backendUrl,
  user,
  interval,                 // { start: Date|string, end: Date|string }
  onIntervalChange,        // (start: Date, end: Date) when user zooms
  loadData = defaultLoadData,
  initialData = null,
  showUpdateButton = true,
  points = 2000,
  showPsd: controlledShowPsd,
  disablePsdButton = false,
  disableShowLegendsButton = false,
  onShowPsdChange,
  showLegends: controlledShowLegends,
  onShowLegendsChange,
  channelLabelMappings = [],
  disabled = false,
  className,
  style
}) {
  const { selectedChannels } = useSelectedChannels(); // expects channel objects with { id, fullname/name }
  const [data, setData] = useState(initialData);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localShowPsd, setLocalShowPsd] = useState(false);
  const [localShowLegends, setLocalShowLegends] = useState(true);
  const [now, setNow] = useState(Date.now);
  const showPsd = controlledShowPsd ?? localShowPsd;
  const showLegends = controlledShowLegends ?? localShowLegends;
  const setShowPsd = onShowPsdChange ?? setLocalShowPsd;
  const setShowLegends = onShowLegendsChange ?? setLocalShowLegends;

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const startISO = useMemo(
    () => (interval?.start ? new Date(interval.start).toISOString() : null),
    [interval?.start]
  );
  const endISO = useMemo(
    () => (interval?.end ? new Date(interval.end).toISOString() : null),
    [interval?.end]
  );

  const channelIds = useMemo(
    () => selectedChannels?.map(c => c.id) ?? [],
    [selectedChannels]
  );
  const selectedChannelsByLabel = useMemo(() => {
    const map = new Map();
    for (const channel of selectedChannels ?? []) {
      const label = getChannelLabel(channel);
      if (label) map.set(label, channel);
    }
    return map;
  }, [selectedChannels]);

  const canUpdate =
    !!backendUrl &&
    !!user.token &&
    !!startISO &&
    !!endISO &&
    channelIds.length > 0 &&
    !loading &&
    !disabled;

  // Manual fetch (used by Update button and zoom)
  const fetchNow = useCallback(
    async (opts = {}) => {
      const start = opts.startISO ?? startISO;
      const end = opts.endISO ?? endISO;
      const ids = opts.channelIds ?? channelIds;

      if (disabled || !backendUrl || !user.token || !start || !end || !ids?.length) return;

      setLoading(true);
      setErr(null);
      try {
        const payload = await loadData({
          backendUrl,
          user,
          channelIds: ids,
          start,
          end,
          points: (showPsd ? points * 5 : points) // more points for PSD to get better frequency resolution
        });
        setData(payload);
      } catch (e) {
        setErr(e?.message || 'Failed to load data');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [backendUrl, user, startISO, endISO, channelIds, points, loadData, showPsd, disabled]
  );

  // Chart zoom -> update parent interval + fetch immediately with zoomed range
  const handleZoom = useCallback(
    (start, end) => {
      if (disabled) return;
      const s = new Date(start);
      const e = new Date(end);
      onIntervalChange?.(s, e);
      // fetch immediately using the zoomed range (doesn't wait for parent re-render)
      fetchNow({ startISO: s.toISOString(), endISO: e.toISOString() });
    },
    [onIntervalChange, fetchNow, disabled]
  );

  const plotPanels = useMemo(() => {
    const panels = new Map();

    const ensurePanel = (channel, fallbackKey = '__unknown__') => {
      const key = channel ? getValueTypeKey(channel) : fallbackKey;
      if (!panels.has(key)) {
        panels.set(key, createPlotPanel(channel, key));
      }
      return panels.get(key);
    };

    for (const channel of selectedChannels ?? []) {
      ensurePanel(channel, getValueTypeKey(channel));
    }

    if (!data) {
      return Array.from(panels.values());
    }

    for (const [label, series] of Object.entries(data)) {
      const channel = selectedChannelsByLabel.get(label);
      const panel = ensurePanel(channel);
      const timestamps = Array.isArray(series?.timestamp) ? series.timestamp : [];
      const { rows, hasGap } = buildSeriesRows(series);
      if (rows.length === 0) continue;

      const spanSec =
        timestamps.length > 1
          ? Number(timestamps.at(-1)) - Number(timestamps[0])
          : 0;
      const pxSpan = timestamps.length > 0 && spanSec > 0
        ? (spanSec * 1000 / timestamps.length)
        : 0;

      const displayLabel = getChannelDisplayLabel(label, channelLabelMappings);
      panel.datasets.push({
        label: displayLabel,
        data: rows,
        borderColor: colorFromLabel(label),
        borderWidth: 1,
        fill: false,
        showLine: true,
        pointRadius: 0,
        tension: 0,
        spanGaps: hasGap ? false : (pxSpan > 601000 ? (1.5 * pxSpan) : 601000),
      });
    }

    return Array.from(panels.values()).filter(panel => panel.datasets.length > 0);
  }, [data, selectedChannels, selectedChannelsByLabel, channelLabelMappings]);

  const needChannels = channelIds.length === 0;
  const needInterval = !startISO || !endISO;

  return (
    <div className={`${styles.plotWrapper ?? ''} ${className ?? ''}`} style={style}>
      {/* Local action bar: only Update is here (selectors live elsewhere) */}
      <div className={styles.plotNavbar ?? ''} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span>
          {showUpdateButton && (
            <button
              type="button"
              onClick={() => fetchNow()}
              disabled={!canUpdate}
            >
              {loading ? 'Loading…' : 'Update'}
            </button>
          )}
          {disablePsdButton ? null : (
            <label>
              <input type="checkbox" checked={showPsd} disabled={disabled} onChange={(e) => setShowPsd(e.target.checked)} />
              Show PSD
            </label>
          )}
          {disableShowLegendsButton ? null : (
              <label>
                <input type="checkbox" checked={showLegends} disabled={disabled} onChange={(e) => setShowLegends(e.target.checked)} />
                Show Legends
              </label>
          )}
        </span>
        {/* {lastUpdatedAt && (
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            Updated {lastUpdatedAt.toLocaleTimeString()}
          </span>
        )}
        {(!needChannels && !needInterval && !loading) ? (
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            ({channelIds.length} channel{channelIds.length !== 1 ? 's' : ''}, {new Date(startISO).toLocaleString()} → {new Date(endISO).toLocaleString()})
          </span>
        ) : null} */}
      </div>

      <div className={styles.chartArea ?? ''}>
        {needChannels && <div style={{ opacity: 0.7 }}>Select one or more channels.</div>}
        {!needChannels && needInterval && <div style={{ opacity: 0.7 }}>Choose a time interval.</div>}
        {err && <div style={{ color: 'tomato' }}>{String(err)}</div>}
        {loading && <h3>Loading data…</h3>}

        {!loading && !err && data && plotPanels.length > 0 && (
          <div className={styles.panelStack ?? ''}>
            {plotPanels.map((panel) => (
              <div key={panel.key} className={`${styles.plotPair} ${showPsd ? styles.withPsd : styles.withoutPsd}`}>
                <section className={styles.plotPanel ?? ''}>
                  <div className={styles.panelHeader ?? ''}>
                    <div className={styles.panelMeta ?? ''}>
                      {panel.datasets.length} channel{panel.datasets.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className={styles.panelChart ?? ''}>
                    <Line
                      data={{ datasets: panel.datasets }}
                      options={getChartOptions({
                        yAxisLabel: panel.yAxisLabel,
                        yAxisMin: null,
                        yAxisMax: null,
                        yAxisDecimals: null,
                        handleZoom,
                        xAxisLabel: 'Date',
                        showLegends,
                        now,
                      })}
                    />
                  </div>
                </section>
                {showPsd && (
                  <PsdComponent
                    data={data}
                    valueTypeKey={panel.key}
                    channelLabelMappings={channelLabelMappings}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && !err && data && plotPanels.length === 0 && (
          <div style={{ opacity: 0.7 }}>No data returned for the selected channels and interval.</div>
        )}
      </div>
    </div>
  );
}

export { buildSeriesRows };
